import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { photoFilenames } from "../constants";

const PictureCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [carouselImages, setCarouselImages] = useState([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Use useEffect to randomize photos once on component mount
  useEffect(() => {
    // Function to shuffle array (Fisher-Yates algorithm)
    const shuffleArray = (array) => {
      const newArray = [...array];
      for (let i = newArray.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
      }
      return newArray;
    };

    // Shuffle the photoFilenames array and take the first 10
    const shuffledPhotos = shuffleArray(photoFilenames).slice(0, 10);

    // Import the randomly selected photos
    const randomPhotos = shuffledPhotos;

    setCarouselImages(randomPhotos);
  }, []); // Empty dependency array ensures this runs only once on mount

  // Track window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const goToPrevious = () => {
    const isFirstImage = currentIndex === 0;
    const newIndex = isFirstImage
      ? carouselImages.length - 1
      : currentIndex - 1;
    setCurrentIndex(newIndex);
  };

  const goToNext = () => {
    const isLastImage = currentIndex === carouselImages.length - 1;
    const newIndex = isLastImage ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
  };

  // Get indices for side images
  const getAdjacentIndices = (current, total, count) => {
    const indices = [];
    for (let i = 1; i <= count; i++) {
      // Previous indices
      indices.push((current - i + total) % total);
      // Next indices
      indices.push((current + i) % total);
    }
    return indices;
  };

  const getSideImageCount = () => {
    if (windowWidth >= 768) return 1;
    return 0; // sm screens: no side images
  };

  const sideImageCount = getSideImageCount();
  const adjacentIndices = getAdjacentIndices(
    currentIndex,
    carouselImages.length,
    sideImageCount
  );

  // Previous indices are the first half of adjacentIndices
  const prevIndices = adjacentIndices.slice(0, sideImageCount);
  // Next indices are the second half of adjacentIndices
  const nextIndices = adjacentIndices.slice(sideImageCount);

  // Show loading state while images are being prepared
  if (carouselImages.length === 0) {
    return (
      <div className="py-12 sm:px-[80px] px-[12px] w-full mx-auto text-center">
        <h2 className="font-sora text-3xl lg:text-[36px] leading-[150%] font-semibold text-[#2F4171] text-center">
          Pictures from the event
        </h2>
        <p className="text-[#1C1C1C] font-[300] leading-[150%] mt-2">
          Loading photos...
        </p>
      </div>
    );
  }

  return (
    <div className="py-12 sm:px-[80px] px-[12px] w-full mx-auto">
      {/* Heading and credits */}
      <div className="text-center mb-6">
        <h2 className="font-sora text-3xl lg:text-[36px] leading-[150%] font-semibold text-[#2F4171] text-center">
          Pictures from the event
        </h2>
        <p className="text-[#1C1C1C] font-[300] leading-[150%]">
          Credits: "The Photographers"
        </p>
      </div>

      {/* Carousel container */}
      <div className="relative flex items-center justify-center h-[250px] sm:h-[300px] md:h-[400px] lg:h-[450px] overflow-hidden">
        {/* Left blurred images - only show on md screens and above */}
        <div className="hidden h-[75%] self-center md:flex absolute inset-y-0 left-0 w-1/4 opacity-30 overflow-hidden">
          <div className="w-full h-full justify-center items-center flex flex-col md:flex-row">
            {prevIndices.map((index, i) => (
              <div
                key={`prev-${index}`}
                className={`h-full ${
                  prevIndices.length > 1 ? "w-1/2" : "w-full"
                }`}
                style={{
                  opacity: 0.7 - i * 0.3, // First image more visible than second
                  zIndex: 10 - i,
                }}
              >
                <img
                  src={carouselImages[index] || "/placeholder.svg"}
                  alt="Previous event"
                  className="w-full h-full object-cover"
                  style={{ filter: `blur(${2 + i}px)` }}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Left navigation button */}
        <button
          onClick={goToPrevious}
          className="absolute left-2 sm:left-4 z-20 shadow-md hover:shadow-lg rounded-full"
          aria-label="Previous image"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="64"
            height="64"
            viewBox="0 0 64 64"
            fill="none"
          >
            <rect width="64" height="64" rx="32" fill="white" />
            <path
              d="M32.0002 53.76C19.9682 53.76 10.2402 44.032 10.2402 32C10.2402 19.968 19.9682 10.24 32.0002 10.24C44.0322 10.24 53.7602 19.968 53.7602 32C53.7602 44.032 44.0322 53.76 32.0002 53.76ZM32.0002 12.8C21.3762 12.8 12.8002 21.376 12.8002 32C12.8002 42.624 21.3762 51.2 32.0002 51.2C42.6242 51.2 51.2002 42.624 51.2002 32C51.2002 21.376 42.6242 12.8 32.0002 12.8Z"
              fill="#2F4171"
            />
            <path
              d="M32.3838 44.416L19.9678 32L32.3838 19.584L34.1758 21.376L23.5518 32L34.1758 42.624L32.3838 44.416Z"
              fill="#2F4171"
            />
            <path
              d="M21.7598 30.72H43.5198V33.28H21.7598V30.72Z"
              fill="#2F4171"
            />
          </svg>
        </button>

        {/* Main center image */}
        <div className="relative z-10 h-full max-w-[80%] sm:max-w-[70%] md:max-w-[60%] lg:max-w-[50%] mx-auto flex items-center justify-center">
          <img
            src={carouselImages[currentIndex] || "/placeholder.svg"}
            alt="Event"
            className="max-h-full max-w-full object-contain scale-100 sm:scale-110"
          />
        </div>

        {/* Right navigation button */}
        <button
          onClick={goToNext}
          className="absolute right-2 sm:right-4 z-20 shadow-md hover:shadow-lg rounded-full"
          aria-label="Next image"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="64"
            height="64"
            viewBox="0 0 64 64"
            fill="none"
          >
            <rect
              width="64"
              height="64"
              rx="32"
              transform="matrix(-1 0 0 1 64 0)"
              fill="white"
            />
            <path
              d="M31.9998 53.76C44.0318 53.76 53.7598 44.032 53.7598 32C53.7598 19.968 44.0318 10.24 31.9998 10.24C19.9678 10.24 10.2398 19.968 10.2398 32C10.2398 44.032 19.9678 53.76 31.9998 53.76ZM31.9998 12.8C42.6238 12.8 51.1998 21.376 51.1998 32C51.1998 42.624 42.6238 51.2 31.9998 51.2C21.3758 51.2 12.7998 42.624 12.7998 32C12.7998 21.376 21.3758 12.8 31.9998 12.8Z"
              fill="#2F4171"
            />
            <path
              d="M31.6162 44.416L44.0322 32L31.6162 19.584L29.8242 21.376L40.4482 32L29.8242 42.624L31.6162 44.416Z"
              fill="#2F4171"
            />
            <path
              d="M42.2402 30.72H20.4802V33.28H42.2402V30.72Z"
              fill="#2F4171"
            />
          </svg>
        </button>

        {/* Right blurred images - only show on md screens and above */}
        <div className="hidden h-[75%] self-center md:flex absolute inset-y-0 right-0 w-1/4 opacity-30 overflow-hidden">
          <div className="w-full h-full justify-center items-center flex flex-col md:flex-row">
            {nextIndices.map((index, i) => (
              <div
                key={`next-${index}`}
                className={`h-full ${
                  nextIndices.length > 1 ? "w-1/2" : "w-full"
                }`}
                style={{
                  opacity: 0.7 - i * 0.3, // First image more visible than second
                  zIndex: 10 - i,
                }}
              >
                <img
                  src={carouselImages[index] || "/placeholder.svg"}
                  alt="Next event"
                  className="w-full h-full object-cover"
                  style={{ filter: `blur(${2 + i}px)` }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Image counter */}
      <div className="text-center mt-4 text-sm text-gray-500">
        {currentIndex + 1} / {carouselImages.length}
      </div>

      {/* Browse all photos button */}
      <div className="text-center mt-6">
        <Link
          to="/event/ai-data-stack-connect-2025/gallery"
          className="btn-sweep btn-sweep-indigo text-white font-medium py-2 sm:py-3 px-4 sm:px-6 rounded-[8px] text-sm sm:text-base"
          rel="noreferrer"
        >
          Browse all photos
        </Link>
      </div>
    </div>
  );
};

export default PictureCarousel;
